import React from "react";

import { useIsSuperUser } from "shared/hooks/useIsSuperUser";
import { formatDate } from "shared/lib/dateTime";
import { formatPhoneNumber } from "shared/lib/phoneNumber";
import { Banner } from "shared/ui/Banner";
import { Button } from "shared/ui/Button";
import { ConfirmDialog } from "shared/ui/ConfirmDialog";
import { EditIcon } from "shared/ui/icons";
import { IconButton } from "shared/ui/IconButton";
import { NotificationSnackbar } from "shared/ui/NotificationSnackbar";
import { Skeleton } from "shared/ui/Skeleton";
import { Tabs } from "shared/ui/Tabs";

import { AccountPhoneDialog } from "./components/AccountPhoneDialog";
import { RenameDialog } from "./components/RenameDialog";
import { AccountsTab } from "./components/tabs/AccountsTab";
import { FeaturesTab } from "./components/tabs/FeaturesTab";
import { PartnerTab } from "./components/tabs/PartnerTab";
import { PaymentsTab } from "./components/tabs/PaymentsTab";
import { TariffTab } from "./components/tabs/TariffTab";
import { BranchDetailsTab, PARTNER_ROLE_TABS, TABS } from "./constants";
import { useBranchAccounts } from "./hooks/useBranchAccounts";
import { useBranchDetailsState } from "./hooks/useBranchDetailsState";
import { useInvoiceDocuments } from "./hooks/useInvoiceDocuments";
import { useServiceOrders } from "./hooks/useServiceOrders";
import { Header, Name, StatusBadge, TabContent, TariffSummary } from "./styles";

const STATUS_TONES = {
    paid: "success",
    overdue: "warning",
    expired: "danger",
} as const;

const STATUS_LABELS = {
    paid: "Оплачен",
    overdue: "Просрочен",
    expired: "Истёк",
};

interface BranchDetailsProps {
    branchId: number;
}

export const BranchDetails = ({ branchId }: BranchDetailsProps) => {
    const isSuperUser = useIsSuperUser();
    const state = useBranchDetailsState(branchId);
    const serviceOrders = useServiceOrders(branchId, state.notify, state.refreshBranch);
    const branchAccounts = useBranchAccounts(branchId, state.notify, isSuperUser);
    const invoiceDocuments = useInvoiceDocuments(state.notify, serviceOrders.replaceInvoice);

    if (state.loading) return <Skeleton height="320px" />;

    if (!state.branch) {
        return (
            <Banner tone="danger" title="Ресторан не найден">
                <Button size="small" variant="outlined" color="neutral" onClick={state.onRetry}>
                    Повторить
                </Button>
            </Banner>
        );
    }

    const { branch } = state;
    const tabs = isSuperUser ? TABS : TABS.filter(({ key }) => PARTNER_ROLE_TABS.includes(key));

    const renderTab = (tab: BranchDetailsTab) => {
        if (tab === "tariff") {
            return (
                <TariffTab
                    tariff={branch.tariff}
                    saving={state.saving}
                    onSave={state.onSaveTariff}
                />
            );
        }

        if (tab === "accounts") {
            return (
                <AccountsTab
                    accounts={branchAccounts.accounts}
                    loading={branchAccounts.loading}
                    error={branchAccounts.error}
                    saving={branchAccounts.saving}
                    onAdd={branchAccounts.onStartAdding}
                    onEdit={branchAccounts.onStartEditing}
                    onDelete={branchAccounts.onStartDeleting}
                    onRetry={branchAccounts.onRetry}
                />
            );
        }

        if (tab === "features") {
            return (
                <FeaturesTab
                    features={branch.features}
                    saving={state.saving}
                    onSave={state.onSaveFeatures}
                />
            );
        }

        if (tab === "partner") {
            return (
                <PartnerTab
                    partner={branch.partner}
                    saving={state.saving}
                    onSave={state.onSavePartner}
                    onRemove={state.onRemovePartner}
                />
            );
        }

        return (
            <PaymentsTab
                orders={serviceOrders.orders}
                loading={serviceOrders.loading}
                error={serviceOrders.error}
                savingInvoiceId={serviceOrders.savingInvoiceId}
                canChangeInvoiceStatus={isSuperUser}
                busyInvoiceId={invoiceDocuments.busyInvoiceId}
                onOpenInvoice={invoiceDocuments.onOpenInvoice}
                onOpenAct={invoiceDocuments.onOpenAct}
                onChangeInvoiceStatus={serviceOrders.changeInvoiceStatus}
                onRetry={serviceOrders.onRetry}
            />
        );
    };

    return (
        <>
            <Header>
                <StatusBadge $tone={STATUS_TONES[branch.tariff.paymentStatus]}>
                    {STATUS_LABELS[branch.tariff.paymentStatus]}
                </StatusBadge>
                <Name>{branch.name}</Name>
                {isSuperUser && (
                    <IconButton label="Переименовать" onClick={state.onStartRenaming}>
                        <EditIcon />
                    </IconButton>
                )}
            </Header>

            <TariffSummary>
                <span>{branch.tariff.title}</span>
                <span>Следующая оплата: {formatDate(branch.tariff.nextPaymentDate)}</span>
                <span>Меню работает до: {formatDate(branch.tariff.menusActiveTillDate)}</span>
            </TariffSummary>

            <Tabs<BranchDetailsTab> value={state.tab} tabs={tabs} onChange={state.onChangeTab} />
            <TabContent>{renderTab(state.tab)}</TabContent>

            <RenameDialog
                open={state.isRenaming}
                name={branch.name}
                saving={state.saving}
                onClose={state.onCancelRenaming}
                onSubmit={state.onRename}
            />

            <AccountPhoneDialog
                open={branchAccounts.editing !== null}
                account={branchAccounts.editing?.account ?? null}
                saving={branchAccounts.saving}
                onClose={branchAccounts.onCancelEditing}
                onSubmit={branchAccounts.onSubmitPhoneNumber}
            />

            <ConfirmDialog
                open={branchAccounts.deleting !== null}
                title="Удалить аккаунт?"
                description={`Аккаунт ${formatPhoneNumber(
                    branchAccounts.deleting?.phoneNumber ?? ""
                )} потеряет доступ к кабинету заведения. Отменить это будет нельзя.`}
                confirmLabel="Удалить"
                tone="danger"
                saving={branchAccounts.saving}
                onClose={branchAccounts.onCancelDeleting}
                onConfirm={branchAccounts.onConfirmDeleting}
            />

            <NotificationSnackbar {...state.notify.snackbarProps} />
        </>
    );
};
